import { useMemo, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import type { PostPlatform, PublishingAccount } from '../../../types/publishing';

// Organic Social Reporting — searchable client picker. Reuses the calendar's
// postable-account set (FB/IG/GBP connected). Lightweight combobox: a filter
// input + a scrollable list, connected channels shown as brand glyphs.
export function SocialAccountPicker({
  accounts,
  value,
  onChange,
}: {
  accounts: PublishingAccount[];
  value: string;
  onChange: (accountId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = accounts.find((a) => a.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return accounts.slice(0, 60);
    return accounts
      .filter(
        (a) => a.name.toLowerCase().includes(q) || (a.brandName ?? '').toLowerCase().includes(q),
      )
      .slice(0, 60);
  }, [accounts, query]);

  if (selected && !open) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900">{selected.name}</p>
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            {selected.brandName ? <span className="truncate">{selected.brandName}</span> : null}
            <ConnectedGlyphs account={selected} />
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Input
        autoFocus
        value={query}
        placeholder="Search clients…"
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white">
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-sm text-slate-400">No connected clients match.</p>
        )}
        {filtered.map((a) => (
          <button
            key={a.id}
            type="button"
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
            onClick={() => {
              onChange(a.id);
              setOpen(false);
              setQuery('');
            }}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm text-slate-900">{a.name}</span>
              {a.brandName && <span className="block truncate text-xs text-slate-400">{a.brandName}</span>}
            </span>
            <ConnectedGlyphs account={a} />
          </button>
        ))}
      </div>
    </div>
  );
}

function ConnectedGlyphs({ account }: { account: PublishingAccount }) {
  const platforms = (Object.keys(account.connected) as PostPlatform[]).filter(
    (p) => account.connected[p],
  );
  return (
    <span className="flex items-center gap-1">
      {platforms.map((p) => (
        <ChannelIcon key={p} channel={p} className="h-3.5 w-3.5" />
      ))}
    </span>
  );
}
